import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ArchiveStyles.css';
import '../users/ServiceUnits.css';
import { REGLES_CONSERVATION, REGLES_CONSERVATION_IMPORT_ZIP, REGLE_CONSERVATION } from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';

const supportLabels = {
  papier: 'Papier',
  pdfa: 'PDF/A',
  image: 'Image',
  ged: 'Base GED',
};

const destinationLabels = {
  conservation_permanente: 'Conservation permanente',
  destruction_securisee: 'Destruction sécurisée',
  transfert: 'Transfert',
};

const formatSupport = (values) => {
  if (!Array.isArray(values) || values.length === 0) return '-';
  return values.map((value) => supportLabels[value] || value).join(', ');
};

const ReglesConservationList = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const canManage = useMemo(() => {
    const roles = Array.isArray(session.roles) ? session.roles : [];
    return ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].some((role) => roles.includes(role));
  }, [session.roles]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const fetchRegles = useCallback(() => {
    setLoading(true);
    axios
      .get(REGLES_CONSERVATION(), authHeaders)
      .then((res) => {
        setRegles(res.data?.regles || []);
      })
      .catch(() => ToastError('Impossible de charger les règles de conservation'))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => {
    fetchRegles();
  }, [fetchRegles]);

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer cette règle de conservation ?')) return;
    axios
      .delete(REGLE_CONSERVATION(id), authHeaders)
      .then(() => {
        ToastConfirm('Règle supprimée');
        fetchRegles();
      })
      .catch((err) => ToastError(err.response?.data?.message || 'Suppression impossible'));
  };

  const importRules = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      ToastError('Le ZIP ne doit pas dépasser 2 Mo.');
      event.target.value = '';
      return;
    }
    const data = new FormData();
    data.append('rulesZip', file);
    setImporting(true);
    try {
      const response = await axios.post(REGLES_CONSERVATION_IMPORT_ZIP(), data, authHeaders);
      const { created = 0, skipped = 0 } = response.data || {};
      ToastConfirm(`${created} règle(s) importée(s), ${skipped} déjà présente(s).`);
      fetchRegles();
    } catch (error) {
      ToastError(error.response?.data?.message || "Impossible d'importer les règles.");
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Règles de conservation" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9 retention-list-page">
              <div className="service-admin-header retention-service-header">
                <div>
                  <h1>Règles de conservation</h1>
                  <p>Consultez les durées, les supports et le sort final définis pour vos archives.</p>
                </div>
                <div className="retention-list-actions">
                  {canManage && <>
                    <input
                      ref={importInputRef}
                      type="file"
                      accept=".zip,application/zip"
                      className="d-none"
                      aria-label="Fichier ZIP de règles de conservation"
                      onChange={importRules}
                    />
                    <button
                      className="btn btn-outline-primary btn-file-action"
                      type="button"
                      disabled={importing}
                      onClick={() => importInputRef.current?.click()}
                    >
                      <i className="ri-upload-2-line" aria-hidden="true" /> {importing ? 'Import en cours…' : 'Importer un ZIP'}
                    </button>
                  </>}
                  {canManage && <button
                    className="btn btn-primary submit-btn"
                    type="button"
                    onClick={() => navigate('/apps/in/archives/regles-conservation/add')}
                  >
                    <i className="ri-add-circle-line" aria-hidden="true" /> Créer une règle
                  </button>}
                </div>
              </div>

              <div className="retention-list-card">
                <div className="retention-list-card-heading">
                  <h2>Liste des règles</h2>
                  <span>{regles.length} règle{regles.length > 1 ? 's' : ''}</span>
                </div>
                      <div className="table-responsive">
                        <table className="table table-hover table-center syidocs-data-table mb-0">
                          <thead>
                            <tr>
                              <th>Règle</th>
                              <th>Durée</th>
                              <th>Support</th>
                              <th>Sort final</th>
                              <th>Responsable</th>
                              <th className="text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="6">
                                  <div className="col-12 item simpleSpinner">
                                    {SimpleSpinner()}
                                    <span>Chargement...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : regles.length === 0 ? (
                              <tr>
                                <td colSpan="6">
                                  <div className="alert alert-info mb-0">Aucune règle disponible.</div>
                                </td>
                              </tr>
                            ) : (
                              regles.map((regle) => (
                                <tr key={regle.id}>
                                  <td className="retention-rule-name">
                                    <div className="fw-bold">{regle.code ? `${regle.code} — ` : ''}{regle.regle || '-'} <small className="text-muted">v{regle.version || 1}</small></div>
                                    {regle.description && <small className="text-muted d-block">{regle.description}</small>}
                                    {regles.some((candidate) => Number(candidate.supersedesRuleId) === Number(regle.id)) && <small className="text-muted">Version remplacée</small>}
                                  </td>
                                  <td>
                                    {regle.isUnlimited
                                      ? 'Illimitée'
                                      : regle.retentionDurationYears
                                        ? `${regle.retentionDurationYears} an(s)`
                                        : '-'}
                                  </td>
                                  <td>{formatSupport(regle.support)}</td>
                                  <td>{destinationLabels[regle.finalDestination] || '-'}</td>
                                  <td>{regle.responsable || '-'}</td>
                                  <td className="text-right retention-row-actions-cell">
                                    <div className="retention-row-actions">
                                      {canManage && <button
                                        className="syidocs-table-icon-button"
                                        type="button"
                                        aria-label={`Éditer ${regle.regle || 'la règle'}`}
                                        title="Éditer"
                                        onClick={() =>
                                          navigate(`/apps/in/archives/regles-conservation/${regle.id}`)
                                        }
                                      >
                                        <i className="ri-pencil-line" aria-hidden="true" />
                                      </button>}
                                      {canManage && <button
                                        className="syidocs-table-icon-button is-danger"
                                        type="button"
                                        aria-label={`Supprimer ${regle.regle || 'la règle'}`}
                                        title="Supprimer"
                                        onClick={() => handleDelete(regle.id)}
                                      >
                                        <i className="ri-delete-bin-line" aria-hidden="true" />
                                      </button>}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ReglesConservationList;
